import {
  Card,
  CardBody,
  Image,
  Stack,
  Heading,
  Text,
  Divider,
  CardFooter,
  Flex,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  VStack,
  HStack,
  Link,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import ReviewCard from './ReviewCard';
import {
  FaMapMarkerAlt,
  FaRegSmileBeam,
  FaRegSmile,
  FaRegSadCry,
  FaPhone,
  FaGlobeEurope,
} from 'react-icons/fa';

function InsightsCard({ place, setPlace }) {
  const {
    placeName,
    address,
    location,
    phoneNumber,
    photos,
    rating,
    userRatingCount,
    summary,
    website,
    gMapsUri,
    reviews,
  } = place;
  const cardBg = useColorModeValue('#FFFFFF', 'black');
  const positiveColor = useColorModeValue('green.400', 'green.300');
  const negativeColor = useColorModeValue('red.400', 'red.300');
  const overallImpressionColor = useColorModeValue('blue.400', 'blue.300');
  const positiveBg = useColorModeValue('green.50', 'green.900');
  const negativeBg = useColorModeValue('red.50', 'red.900');
  const overallImpressionBg = useColorModeValue('blue.50', 'blue.900');
  const focusPlace = () =>
    setPlace({ lat: location.latitude, lng: location.longitude });

  const handleCardClick = event => {
    if (!event.target.closest('a, button')) focusPlace();
  };

  return (
    <Card
      maxW="320px"
      mx="auto"
      my="1"
      bg={cardBg}
      cursor="pointer"
      overflow="hidden"
      onClick={handleCardClick}
      w="calc(100% - 16px)"
    >
      <CardBody>
        <Flex gap={2} overflowX="auto" pb={1} px={1} w="full">
          {photos.map(photo => (
            <Image
              key={photo.name.split('/').pop()}
              src={`https://maps.googleapis.com/maps/api/place/photo?maxwidth=${900}&maxheight=${250}&photoreference=${photo.name
                .split('/')
                .pop()}&key=${process.env.REACT_APP_MAP_INSIGHT_GCP_KEY}`}
              alt={photo.name.split('/').pop()}
              display="block"
              borderRadius="lg"
              flex="0 0 240px"
              h="180px"
              objectFit="cover"
              w="240px"
            />
          ))}
        </Flex>
        <Stack mt="6" spacing="3">
          <Heading
            fontWeight={'semibold'}
            _hover={{ textDecoration: 'underline' }}
            as="a"
            href={gMapsUri}
            target="_blank"
            size="md"
          >
            {placeName}
          </Heading>
          <Tabs isFitted variant="enclosed">
            <TabList mb="1em">
              <Tab>Summary</Tab>
              <Tab>Reviews</Tab>
            </TabList>
            <TabPanels>
              <TabPanel maxHeight={'300px'} overflowY={'auto'}>
                <VStack spacing={3} align="stretch" textAlign="left">
                  {summary?.key_positives?.length > 0 && (
                    <Box
                      bg={positiveBg}
                      borderLeft="4px solid"
                      borderColor={positiveColor}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack mb={2} color={positiveColor}>
                        <FaRegSmileBeam aria-hidden="true" />
                        <Heading size="sm">Highlights</Heading>
                      </HStack>
                      {summary.key_positives.map(keyPositive => (
                        <Text key={keyPositive} fontSize="sm" mb={1}>
                          • {keyPositive}
                        </Text>
                      ))}
                    </Box>
                  )}
                  {summary?.key_negatives?.length > 0 && (
                    <Box
                      bg={negativeBg}
                      borderLeft="4px solid"
                      borderColor={negativeColor}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack mb={2} color={negativeColor}>
                        <FaRegSadCry aria-hidden="true" />
                        <Heading size="sm">Watch out for</Heading>
                      </HStack>
                      {summary.key_negatives.map(keyNegative => (
                        <Text key={keyNegative} fontSize="sm" mb={1}>
                          • {keyNegative}
                        </Text>
                      ))}
                    </Box>
                  )}
                  {summary?.overall_impression && (
                    <Box
                      bg={overallImpressionBg}
                      borderLeft="4px solid"
                      borderColor={overallImpressionColor}
                      borderRadius="md"
                      p={3}
                    >
                      <HStack mb={2} color={overallImpressionColor}>
                        <FaRegSmile aria-hidden="true" />
                        <Heading size="sm">Overall take</Heading>
                      </HStack>
                      <Text fontSize="sm">{summary.overall_impression}</Text>
                    </Box>
                  )}
                  {!summary?.key_positives?.length &&
                    !summary?.key_negatives?.length &&
                    !summary?.overall_impression && (
                      <Text color="gray.500" fontSize="sm">
                        AI insights are not available for this place yet.
                      </Text>
                    )}
                </VStack>
              </TabPanel>
              <TabPanel maxHeight={'300px'} overflowY={'auto'}>
                {reviews &&
                  reviews.length > 0 &&
                  reviews.map((review, index) => (
                    <ReviewCard
                      key={review.authorName + review.publishTime}
                      {...review}
                      index={index}
                    />
                  ))}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Stack>
      </CardBody>

      <Divider />
      <CardFooter px={5} py={4}>
        <VStack align="stretch" spacing={3} textAlign="left" w="full">
          <HStack align="flex-start" spacing={3} w="full">
            <Box color="gray.500" pt={1}>
              <FaMapMarkerAlt aria-hidden="true" />
            </Box>
            <Link
              flex="1"
              href={gMapsUri}
              minW={0}
              target="_blank"
              rel="noreferrer"
              fontSize="sm"
            >
              {address}
            </Link>
          </HStack>
          <HStack spacing={2}>
            <Box color={positiveColor}>
              {rating >= 4.5 ? (
                <FaRegSmileBeam aria-hidden="true" />
              ) : rating > 3 ? (
                <FaRegSmile aria-hidden="true" />
              ) : (
                <FaRegSadCry aria-hidden="true" />
              )}
            </Box>
            <Text fontSize="sm" fontWeight="semibold">
              {rating}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {userRatingCount.toLocaleString()} reviews
            </Text>
          </HStack>
          {phoneNumber && (
            <HStack spacing={3} w="full">
              <Box color="gray.500">
                <FaPhone aria-hidden="true" />
              </Box>
              <Link href={`tel:${phoneNumber.replace(/\s/g, '')}`} fontSize="sm">
                {phoneNumber}
              </Link>
            </HStack>
          )}
          {website && (
            <HStack spacing={3} w="full">
              <Box color="gray.500">
                <FaGlobeEurope aria-hidden="true" />
              </Box>
              <Link
                flex="1"
                href={website}
                minW={0}
                target="_blank"
                rel="noreferrer"
                fontSize="sm"
                noOfLines={1}
              >
                {website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </Link>
            </HStack>
          )}
        </VStack>
      </CardFooter>
    </Card>
  );
}

export default InsightsCard;
